import React, { useEffect, useMemo, useState } from "react";
import { Table, Button, Input, Dropdown, Menu, Spin } from "antd";
import { FaEdit } from "react-icons/fa";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import FilterComponent from "../../../components/utils/FilterComponent";
import getOrderStatus from "../../../components/utils/getOrderStatus";
import updateData from "../../../hooks/updateData";
import axios from "../../../components/utils/Interceptors";

const AllOrders = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = useLocation().search;
  const statusQuery = new URLSearchParams(search).get("status");

  const [orders, setOrders] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(statusQuery ?? -1);

  const user = JSON.parse(localStorage.getItem("user"));

  const getAllOrders = async (status_id = -1) => {
    setLoading(true);
    try {
      const url =
        status_id >= 0
          ? `${process.env.REACT_APP_APIURL}/vendor/getallorder?status=${status_id}`
          : `${process.env.REACT_APP_APIURL}/vendor/getallorder`;

      const res = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });

      const response = await res.data;

      if (response.status_code === 200) {
        const data = response.data.reverse();
        setOrders(data);
        setFilteredItems(data);
      } else {
        setOrders([]);
        setFilteredItems([]);
      }
    } catch (error) {
      toast.error("Error fetching orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllOrders(selectedStatus);
  }, [selectedStatus]);

  const handleSearch = (e) => {
    const text = e.target.value;
    setFilterText(text);
    if (!text) {
      setFilteredItems(orders);
    } else {
      const filtered = orders.filter(
        (item) =>
          item?.order_number?.includes(text) ||
          item?.customer_email?.includes(text)
      );
      setFilteredItems(filtered);
    }
  };

  const handleToTrack = (id) => {
    if (id) {
      navigate("/vendor/orders/track-order", {
        state: { id, token: user?.token },
      });
    } else {
      toast.error("Tracking not possible");
    }
  };

  const handleReciptDownload = async ({ triackingId }) => {
    try {
      setLoading(true);
      const baseUrl = process.env.REACT_APP_APIURL;
      const res = await axios.get(
        `${baseUrl}/vendor/trackorder?tracking_id=${triackingId}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      const response = await res.data;

      if (response.tracking_data) {
        const link = document.createElement("a");
        link.href = response.aws_label;
        link.download = "Receipt.pdf";
        link.click();
      } else {
        toast.error("Tracking ID is not correct");
      }
    } catch (err) {
      toast.error("Failed to download receipt");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async ({ item, value }) => {
    try {
      setLoading(true);
      const data = {
        order_id: item.order_id,
        order_status: value,
      };

      const res = await updateData("vendor/updateorder", data);
      if (res.status_code === 200) {
        toast.success("Status updated!");
        const updated = orders.map((order) =>
          order.order_id === item.order_id
            ? { ...order, order_status: value }
            : order
        );
        setOrders(updated);
        setFilteredItems(updated);
      } else {
        toast.error(res.message || res.err);
      }
    } finally {
      setLoading(false);
    }
  };

  const menu = (item) => (
    <Menu>
      <Menu.Item key="view">
        <Link to={`/vendor/orders/order_details/${item.order_id}`}>
          {t("View_Details")}
        </Link>
      </Menu.Item>
      {item.torod_tracking_id && (
        <>
          <Menu.Item key="track" onClick={() => handleToTrack(item.torod_tracking_id)}>
            {t("Track")}
          </Menu.Item>
          <Menu.Item
            key="download"
            onClick={() =>
              handleReciptDownload({
                triackingId: item.torod_tracking_id,
              })
            }
          >
            {t("Download Receipt")}
          </Menu.Item>
        </>
      )}
      {item.order_status === 0 && (
        <>
          <Menu.Item onClick={() => updateStatus({ item, value: 1 })}>
            Confirm Shipped
          </Menu.Item>
          <Menu.Item onClick={() => updateStatus({ item, value: 9 })}>
            {t("V_allOrder_or_cl")}
          </Menu.Item>
        </>
      )}
    </Menu>
  );

  const columns = [
    {
      title: t("V_allOrder_or_id"),
      dataIndex: "order_id",
      key: "torod_order_id",
    },
    {
      title: t("V_allOrder_or_td"),
      dataIndex: "created_on",
      key: "created_on",
      render: (text) => moment(text).format("DD-MMM-YYYY hh:mm a"),
    },
    {
      title: t("V_allOrder_or_c"),
      dataIndex: "customer_email",
      key: "customer_email",
    },
    {
      title: t("V_allOrder_or_a"),
      dataIndex: "order_price",
      key: "order_price",
      render: (value) => `${value} ر.س`,
    },
    {
      title: t("V_allOrder_or_st"),
      dataIndex: "order_status",
      key: "order_status",
      render: (status) => getOrderStatus(status, "", { t }),
    },
    {
      title: t("all_product_dataTable6"),
      key: "actions",
      render: (_, item) => (
        <Dropdown overlay={menu(item)} trigger={["click"]}>
          <Button icon={<FaEdit />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">{t("All_Orders")}</h2>
        <Link
          to="/vendor/add-order"
          className="hidden py-2 px-4 bg-blue-600 text-white rounded"
        >
          {t("V_allOrder_add")}
        </Link>
      </div>

      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <Button onClick={() => setSelectedStatus(-1)}>{t("all")}</Button>
        <Button type="default" danger onClick={() => setSelectedStatus(9)}>
          {t("cancelled")}
        </Button>
        <Button onClick={() => setSelectedStatus(0)}>{t("pending")}</Button>
        <Button onClick={() => setSelectedStatus(1)}>{t("shipped")}</Button>
        <Button onClick={() => setSelectedStatus(2)}>{t("delievered")}</Button>
        <Input
          placeholder={t("Search")}
          value={filterText}
          onChange={handleSearch}
          className="w-64"
        />
      </div>

      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={filteredItems}
          rowKey="order_id"
          pagination={{ pageSize: 10 }}
          locale={{
            emptyText: t("No records"),
          }}
        />
      </Spin>
    </>
  );
};

export default AllOrders;

